import React, { useCallback, useEffect, useState } from "react";
import { collection, doc, getDoc, getDocs, orderBy, query, where } from "firebase/firestore";
import { useNavigate, useParams } from "react-router-dom";
import { db } from "../utils/firebase";
import { useAuth } from "./AuthContext";
import { isAdmin, isCareerCoach, useJobBankRole } from "./jobBankRole";
import PublicProfileCard from "./PublicProfileCard";
import "./PublicProfiles.css";

function getName(profile) {
  return [profile.firstName, profile.lastName].filter(Boolean).join(" ") || profile.displayName || profile.email || "Candidate";
}

export default function PublicCandidateDirectory() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { role, roleLoading } = useJobBankRole(currentUser);
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [loading, setLoading] = useState(true);

  const canViewCandidates = isCareerCoach(role) || isAdmin(role);
  const isOwnProfile = currentUser?.uid && currentUser.uid === uid;

  const loadCandidates = useCallback(async () => {
    try {
      setLoading(true);
      if (uid) {
        const snap = await getDoc(doc(db, "users", uid));
        const data = snap.exists() ? { id: snap.id, ...snap.data() } : null;
        if (data && (canViewCandidates || isOwnProfile) && (data.shareWithCareerCoaches === true || isOwnProfile || isAdmin(role))) {
          setSelectedCandidate(data);
        } else {
          setSelectedCandidate(null);
        }
        return;
      }

      if (!canViewCandidates) return;
      const snapshot = await getDocs(query(
        collection(db, "users"),
        where("shareWithCareerCoaches", "==", true),
        orderBy("lastName")
      ));
      setCandidates(snapshot.docs.map(userDoc => ({ id: userDoc.id, ...userDoc.data() })));
    } catch (error) {
      console.error("Unable to load candidate profiles:", error);
    } finally {
      setLoading(false);
    }
  }, [uid, canViewCandidates, isOwnProfile, role]);

  useEffect(() => { if (!roleLoading) loadCandidates(); }, [roleLoading, loadCandidates]);

  if (loading || roleLoading) return <div className="publicDirectoryPage"><div className="publicProfileEmpty">Loading candidate profiles...</div></div>;

  if (!canViewCandidates && !isOwnProfile) {
    return <div className="publicDirectoryPage"><div className="publicProfileEmpty">Candidate profiles are only available to authorized career coaches.</div></div>;
  }

  if (uid) {
    if (!selectedCandidate) return <div className="publicDirectoryPage"><div className="publicProfileEmpty">Candidate profile not available.</div></div>;
    return (
      <div className="publicDirectoryPage">
        <header className="publicDirectoryHeader"><h1>Candidate Profile</h1><p>Read-only candidate profile. Coach notes and messages are not shown here.</p></header>
        <PublicProfileCard
          title={getName(selectedCandidate)}
          subtitle={selectedCandidate.jobRole || selectedCandidate.primaryOccupation || "Candidate"}
          description={selectedCandidate.primaryEmploymentGoals || ""}
          details={[
            { label: "Location", value: [selectedCandidate.city, selectedCandidate.province].filter(Boolean).join(", ") },
            { label: "Primary Occupation", value: selectedCandidate.primaryOccupation },
            { label: "Secondary Occupation", value: selectedCandidate.secondaryOccupation },
            { label: "Interested in Job Carve-Outs", value: selectedCandidate.interestedInJobCarveOuts },
            { label: "Preferred Hours", value: selectedCandidate.preferredCarveOutHours },
            { label: "Preferred Supervision", value: selectedCandidate.preferredSupervisionLevel },
            { label: "Certifications", value: Array.isArray(selectedCandidate.certifications) ? selectedCandidate.certifications.join(", ") : "" },
            { label: "Resume", value: selectedCandidate.resumeUrl || selectedCandidate.resumeDownloadUrl ? "Available through active coaching relationship or referral" : "Not set" }
          ]}
          actions={<><button onClick={() => navigate("/cecp")}>Open Career Coach Space</button>{isOwnProfile && <button className="secondary" onClick={() => navigate("/profile")}>Edit My Profile</button>}</>}
        />
      </div>
    );
  }

  return (
    <div className="publicDirectoryPage">
      <header className="publicDirectoryHeader"><h1>Shared Candidate Profiles</h1><p>Clients who have permitted career coach visibility.</p></header>
      <div className="publicDirectoryGrid">
        {candidates.map(candidate => (
          <PublicProfileCard
            key={candidate.id}
            title={getName(candidate)}
            subtitle={candidate.jobRole || candidate.primaryOccupation || "Candidate"}
            description={candidate.primaryEmploymentGoals || ""}
            details={[{ label: "Location", value: [candidate.city, candidate.province].filter(Boolean).join(", ") }, { label: "Primary Occupation", value: candidate.primaryOccupation }]}
            actions={<button onClick={() => navigate(`/candidates/${candidate.id}`)}>View Profile</button>}
          />
        ))}
      </div>
    </div>
  );
}
